/**
 * VirtualAccountManagement Component
 * 가상계좌 관리 페이지
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useVirtualAccountData } from '../hooks/useVirtualAccountData';
import { useDelayedLoading } from "@/shared/hooks/useDelayedLoading";
import { usePagination } from "@/shared/hooks/usePagination";
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import FilterSection from './FilterSection';
import VirtualAccountTable from "@/features/system/virtualAccount/components/AccountTable";
import '@/shared/styles/search-form.css';
import '../styles/VirtualAccountManagement.css';

const ITEMS_PER_PAGE = 30;

const VirtualAccountManagement: React.FC = () => {
    const { accounts, loading, isSearchMode, fetchAllAccounts, searchBySuppId, searchByRiderId, resetSearch } = useVirtualAccountData();
    const showLoading = useDelayedLoading(loading);

    const { currentPage, totalPages, currentItems, goToPage, resetPagination } = usePagination({
        items: accounts,
        itemsPerPage: ITEMS_PER_PAGE,
    });

    const [suppId, setSuppId] = useState('');
    const [riderId, setRiderId] = useState('');

    useEffect(() => {
        void fetchAllAccounts();
    }, [fetchAllAccounts]);

    const handleSuppIdSearch = useCallback(async () => {
        if (!suppId.trim()) return;
        await searchBySuppId(suppId.trim());
        resetPagination();
    }, [suppId, searchBySuppId, resetPagination]);

    const handleRiderIdSearch = useCallback(async () => {
        if (!riderId.trim()) return;
        await searchByRiderId(riderId.trim());
        resetPagination();
    }, [riderId, searchByRiderId, resetPagination]);

    const handleReset = useCallback(async () => {
        setSuppId('');
        setRiderId('');
        await resetSearch();
        resetPagination();
    }, [resetSearch, resetPagination]);

    if (showLoading) return <Loading />;

    return (
        <div className="virtual-account-management">
            <div className="page-header">
                <h1 className="page-title">가상계좌 관리</h1>
            </div>

            <FilterSection
                suppId={suppId}
                riderId={riderId}
                isSearchMode={isSearchMode}
                loading={loading}
                onSuppIdChange={setSuppId}
                onRiderIdChange={setRiderId}
                onSuppIdSearch={handleSuppIdSearch}
                onRiderIdSearch={handleRiderIdSearch}
                onReset={handleReset}
            />

            <div className="virtual-account-section-header">
                <h2 className="virtual-account-section-title">
                    {isSearchMode ? '검색 결과' : '가상계좌 목록'}
                </h2>
                <span className="total-count">총 {accounts.length}건</span>
            </div>

            <div className="virtual-account-table-container">
                <VirtualAccountTable accounts={currentItems} />
            </div>

            {totalPages > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={goToPage}
                    />
                </div>
            )}
        </div>
    );
};

export default VirtualAccountManagement;
